import { useState } from 'react';
import { useAppConfig } from '../../../../../hooks/useAppConfig';
import { PhotoSourceModal } from '../../../../components/photo-picker/PhotoSourceModal';
import { DeleteConfirmAlert } from '../DeleteConfirmAlert';
import { PostPhotoStrip } from './PostPhotoStrip';
import type { EditorPhoto } from './types';

interface PostPhotoEditorProps {
  photos: EditorPhoto[];
  onAddFromCamera: () => void;
  onAddFromGallery: () => void;
  /** Receives the `key` of the photo to remove. */
  onRemove: (key: string) => void;
  disabled?: boolean;
}

/**
 * Photos section of the post editor: the strip of photos with the "+" tile, the
 * camera/gallery picker and the delete confirmation. The changes are kept by the
 * editor until the post is saved (see usePostPhotoChanges).
 */
export const PostPhotoEditor = ({
  photos,
  onAddFromCamera,
  onAddFromGallery,
  onRemove,
  disabled = false,
}: PostPhotoEditorProps) => {
  const { postMaxPhotos } = useAppConfig();
  const [showSource, setShowSource] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);

  return (
    <>
      <PostPhotoStrip
        photos={photos}
        maxPhotos={postMaxPhotos}
        disabled={disabled}
        onAdd={() => setShowSource(true)}
        onDelete={setPhotoToDelete}
      />

      <PhotoSourceModal
        isOpen={showSource}
        title="Añadir foto"
        onTakePhoto={() => {
          setShowSource(false);
          onAddFromCamera();
        }}
        onChooseFromGallery={() => {
          setShowSource(false);
          onAddFromGallery();
        }}
        onClose={() => setShowSource(false)}
      />

      <DeleteConfirmAlert
        isOpen={photoToDelete !== null}
        header="Eliminar foto"
        message="¿Seguro que quieres eliminar esta foto?"
        onDidDismiss={() => setPhotoToDelete(null)}
        onConfirm={() => {
          if (photoToDelete) onRemove(photoToDelete);
        }}
      />
    </>
  );
};
